<!-- DesignOnlineSimple.vue -->
<!-- Simplified Design Online component for quick start testing -->
<!-- This uses mock data - real API integration will be added later -->

<template>
  <div class="design-online-simple">
    <!-- Loading State -->
    <div v-if="store.isLoading" class="loading-state">
      <div class="spinner"></div>
      <p>Loading...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="store.error" class="error-state">
      <p class="error-message">{{ store.error }}</p>
      <BaseButton
        text="Try Again"
        variant="secondary"
        @click="store.error = null"
      />
    </div>

    <!-- Chooser State - Create/Upload Buttons -->
    <div v-else-if="store.stage === 'chooser'" class="chooser-state">
      <p class="description">
        Use our online design tool to create your custom imprint.
      </p>
      
      <div class="button-group">
        <BaseButton
          text="Create Design Online"
          variant="primary"
          size="large"
          :disabled="store.isLoading"
          @click="handleCreateClick"
        />
        
        <BaseButton
          text="Upload Your Artwork"
          variant="secondary"
          size="large"
          :disabled="store.isLoading"
          @click="handleUploadClick"
        />
      </div>
      
      <div v-if="!store.isSupported" class="warning-note">
        <p>
          <strong>⚠️ Not Available:</strong> Design Online is not available for this product.
        </p>
      </div>
    </div>

    <!-- Template Select State -->
    <div v-else-if="store.stage === 'template-select'" class="template-select-state">
      <p class="description">Choose a template to start your design:</p>
      <div class="template-grid">
        <button
          v-for="tpl in store.templates"
          :key="tpl.tempid"
          class="template-card"
          @click="handleTemplateSelect(tpl)"
        >
          <img
            v-if="tpl.preview_url"
            :src="tpl.preview_url"
            :alt="tpl.title"
            class="template-card__img"
          />
          <div v-else class="template-card__placeholder">No preview</div>
          <span class="template-card__title">{{ tpl.title }}</span>
        </button>
      </div>
      <BaseButton
        text="Back"
        variant="secondary"
        size="small"
        @click="store.resetToChooser(); store.initialize()"
      />
    </div>

    <!-- Preview State - Show Design -->
    <div v-else-if="store.stage === 'preview'" class="preview-state">
      <!-- Preview Image -->
      <div v-if="store.previewImage" class="preview-image-wrapper">
        <img 
          :src="store.previewImage" 
          alt="Design preview" 
          class="preview-image"
        />
      </div>



      <!-- Approval Checkbox -->
      <div class="approval-section">
        <label class="approval-checkbox">
          <input
            type="checkbox"
            :checked="store.isApproved"
            @change="handleApprovalChange"
          />
          <span class="checkbox-label">
            I confirm I have reviewed & approve my design.
          </span>
        </label>
        
<!--        <div v-if="store.isApproved" class="approval-status">
          ✅ Design approved and ready for cart
        </div>-->
      </div>

        <!-- Action Buttons -->
      <div class="action-buttons">
            <BaseButton
                    text="Edit Design"
                    variant="primary"
                    size="medium"
                    :disabled="!store.editUrl"
                    @click="handleEdit"
            />

            <BaseButton
                    text="Download PDF"
                    variant="outline"
                    size="medium"
                    :disabled="!store.downloadUrl"
                    @click="handleDownload"
            />
        </div>

      <!-- Test Controls (Debug Mode Only) -->
      <div v-if="debugMode" class="test-controls">
        <BaseButton
          text="🔄 Reset (Test)"
          variant="outline"
          size="small"
          @click="handleReset"
        />
      </div>

      <!-- Debug Info (Debug Mode Only) -->
      <div v-if="debugMode" class="debug-info">
        <details>
          <summary>🔍 Debug Info</summary>
          <pre>{{ debugInfo }}</pre>
        </details>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, onBeforeUnmount } from 'vue';
import { useDesignOnlineStore } from '@/features/design/stores/useDesignOnlineStore';
import { useAppStore } from '@/features/imprint/stores/useAppStore';
import { useNewImprintStore } from '@/features/imprint/stores/useNewImprintStore';
import { useSelectionStore } from '@/features/imprint/stores/useSelectionStore';
import BaseButton from '@/features/imprint/components/BaseButton.vue';

const store = useDesignOnlineStore();
const newImprintStore = useNewImprintStore();
const selectionStore = useSelectionStore();

// Emit events
const emit = defineEmits(['design-returned']);

// Initialize on mount
onMounted(async () => {
    console.log('[DO_NAV] 🎬 DesignOnlineSimple.mounted() START');

    // Read OPS product ID from DOM (#ops_product_id element set via Shopify metafield)
    // If not found — store marks isSupported=false and we stop here
    const opsProductId = store.initProductId();
    if (!opsProductId) {
      console.warn('[DesignOnlineSimple] No OPS product ID — Design Online unavailable');
      return;
    }

    console.log('[DO_NAV] Initial store state:', {
        stage: store.stage,
        hasDesign: store.hasDesign,
        isReady: store.isReady,
        isApproved: store.isApproved,
        tokenId: store.tokenId,
        supportCheckDone: store.supportCheckDone,
        supportCheckProductKey: store.supportCheckProductKey,
        isSupported: store.isSupported,
        hasCustomizeUrl: !!store.customizeStudioUrl
    });

    // Check for return from studio
    const { tokenId, exParam } = store.readReturnParamsFromUrl();

    // ✅ Fallback: after EDIT return may contain only token_id
    let finalExParam = exParam;
    if (tokenId && !finalExParam) {
        if (!store.exResponseParam) {
            store.restoreFromStorage();
        }
        finalExParam = store.exResponseParam;
    }

    if (tokenId && finalExParam) {
        console.log('[DesignOnlineSimple] Detected return from studio');
        console.log('[AddToCart] 🔄 Calling handleReturn() with tokenId:', tokenId);
        const success = await store.handleReturn(tokenId, finalExParam);
        
        console.log('[AddToCart] handleReturn() result:', success);
        console.log('[AddToCart] Store state after return:', {
            stage: store.stage,
            hasDesign: store.hasDesign,
            isReady: store.isReady,
            isApproved: store.isApproved,
            tokenId: store.tokenId,
            hasPreview: !!store.previewImage,
            hasPdfPreview: !!store.pdfPreviewUrl,
            hasPrintReady: !!store.printReadyFileUrl
        });

        // ✅ Emit event after successful return
        if (success && store.hasDesign) {
            console.log('[DesignOnlineSimple] 🎉 Emitting design-returned event');
            console.log('[AddToCart] ⚠️ Note: Design loaded but NOT approved yet - user must check the checkbox!');
            emit('design-returned');
        }
    } else {
        // ✅ CRITICAL FIX: Check if store already has design (e.g., from back navigation)
        // If so, skip restore and initialize - just preserve current state
        if (store.stage === 'preview' && store.hasDesign) {
            console.log('[DO_NAV] ✅ Store already has design (back navigation) - preserving state');
            console.log('[DO_NAV] Current state:', {
                stage: store.stage,
                hasDesign: store.hasDesign,
                isApproved: store.isApproved
            });
            // Don't restore, don't initialize - just keep current state
        } else {
            // Try restore from storage
            console.log('[DO_NAV] No URL params, trying to restore from storage...');
            const restored = store.restoreFromStorage();
            console.log('[DO_NAV] Restore from storage result:', restored);

            if (!restored) {
                // Only initialize if stage is 'idle'
                if (store.stage === 'idle') {
                    console.log('[DO_NAV] No saved data, calling initialize()...');
                    store.initialize();
                    console.log('[DO_NAV] After initialize(), stage:', store.stage);
                } else {
                    console.log('[DO_NAV] Stage is', store.stage, '- skipping initialize() to preserve state');
                }
            } else if (store.hasDesign) {
                // ✅ Also emit if restored design from storage
                console.log('[DO_NAV] ✅ Design restored from storage, emitting design-returned');
                emit('design-returned');
            }
        }
    }

    // Phase 1 is now done in CustomizeProductSection.mounted() before this component mounts.
    // Skip if already completed for this product page.
    const currentKey = store.getStableProductKey();
    if (!store.supportCheckDone || store.supportCheckProductKey !== currentKey) {
      console.log('[DO_NAV] Phase 1: running support check (was not done yet)...');
      await store.checkSupport(opsProductId, props.sku, { design_enabled: true, force_probe: false });
    } else {
      console.log('[DO_NAV] Phase 1 already done, skipping');
    }
    
    console.log('[DO_NAV] ✅ DesignOnlineSimple.mounted() END - Final state:', {
        stage: store.stage,
        hasDesign: store.hasDesign,
        isReady: store.isReady,
        isApproved: store.isApproved,
        supportCheckDone: store.supportCheckDone,
        isSupported: store.isSupported
    });
});

onBeforeUnmount(() => {
  console.log('[DesignOnlineSimple] Unmounted');
});

const props = defineProps({
  sku: String,
  debugMode: {
    type: Boolean,
    default: false
  }
});

// Event handlers
async function handleCreateClick() {
  console.log('[DO_NAV] 🎨 handleCreateClick() START');
  console.log('[DO_NAV] Current store state before create:', {
    stage: store.stage,
    hasCustomizeUrl: !!store.customizeStudioUrl,
    supportCheckDone: store.supportCheckDone
  });

  const productId = store.studioConfig.productId;
  if (!productId) {
    console.error('[DesignOnlineSimple] Cannot create — no OPS product ID');
    return;
  }
  
  // ✅ CRITICAL: Save all data before redirect
  newImprintStore.saveToSessionStorage();
  selectionStore.saveToSessionStorage();
  console.log('[DO_NAV] 💾 Saved New Imprint + Selection data before redirect');
  
  // Phase 2: Lazy load URLs if not already fetched
  if (!store.customizeStudioUrl) {
    console.log('[DO_NAV] Phase 2: Fetching URLs (force_probe=true)...');
    await store.checkSupport(productId, props.sku, {
      design_enabled: true,
      force_probe: true  // Call Studio API to get URLs
    });
    console.log('[DO_NAV] Phase 2 complete. hasCustomizeUrl:', !!store.customizeStudioUrl);
  } else {
    console.log('[DO_NAV] customizeStudioUrl already available, skipping Phase 2');
  }

  store.startCreate(props.sku);
  console.log('[DO_NAV] handleCreateClick() END - startCreate called');
}

async function handleUploadClick() {
  console.log('[DesignOnlineSimple] 📤 Upload button clicked');
  const productId = store.studioConfig.productId;
  if (!productId) {
    console.error('[DesignOnlineSimple] Cannot upload — no OPS product ID');
    return;
  }
  
  // ✅ CRITICAL: Save all data before redirect
  newImprintStore.saveToSessionStorage();
  selectionStore.saveToSessionStorage();
  console.log('[DesignOnlineSimple] 💾 Saved New Imprint + Selection data before redirect');
  
  // Phase 2: Lazy load URLs if not already fetched
  if (!store.uploadCenterUrl) {
    console.log('[DesignOnlineSimple] Phase 2: Fetching URLs (force_probe)');
    await store.checkSupport(productId, props.sku, {
      design_enabled: true,
      force_probe: true  // Call Studio API to get URLs
    });
  }
  
  store.startUpload(productId, props.sku);
}

function handleApprovalChange(event) {
  const isChecked = event.target.checked;
  console.log('[AddToCart] ☑️ Approval checkbox changed:', isChecked);
  store.setApproval(isChecked);
  
  console.log('[AddToCart] Store state after approval change:', {
    isApproved: store.isApproved,
    isReady: store.isReady,
    hasDesign: store.hasDesign,
    tokenId: store.tokenId
  });
  
  if (isChecked) {
    console.log('[AddToCart] ✅ Design approved! User can now add to cart.');
  } else {
    console.log('[AddToCart] ❌ Design NOT approved. User must check the box before adding to cart.');
  }
}

function handleEdit() {
  store.openEdit();
}

function handleDownload() {
  store.openDownload();
}

function handleReset() {
  if (confirm('Reset to chooser state?')) {
    store.resetToChooser();
  }
}

function handleTemplateSelect(tpl) {
  console.log('[DO_NAV] Template selected:', tpl);
  store.redirectWithTemplate(props.sku, tpl);
}

// Debug info computed
const debugInfo = computed(() => {
  return {
    stage: store.stage,
    hasDesign: store.hasDesign,
    isReady: store.isReady,
    isApproved: store.isApproved,
    tokenId: store.tokenId,
    source: store.source
  };
});
</script>

<style scoped>
.design-online-simple {
  padding: 24px;
  background: #fff;
  border-radius: 8px;
  min-height: 200px;
    width: 100%;
}

/* Loading State */
.loading-state {
  text-align: center;
  padding: 40px 20px;
}

.spinner {
  width: 40px;
  height: 40px;
  margin: 0 auto 16px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #0066cc;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-state p {
  font-size: 16px;
  color: #666;
}

/* Error State */
.error-state {
  text-align: center;
  padding: 40px 20px;
}

.error-message {
  color: #d32f2f;
  margin-bottom: 16px;
  font-weight: 500;
  font-size: 16px;
}

/* Chooser State */
.chooser-state {
  text-align: center;
}

.description {
  font-size: 16px;
  color: #333;
  margin-bottom: 24px;
  line-height: 1.6;
}

.button-group {
  display: flex;
  gap: 16px;
  justify-content: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.warning-note {
  margin-top: 24px;
  padding: 16px;
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 6px;
  text-align: left;
}

.warning-note p {
  margin: 0;
  font-size: 14px;
  color: #856404;
  line-height: 1.5;
}

.warning-note strong {
  font-weight: 600;
}

/* Preview State */
.preview-state {
  text-align: center;
}

.preview-image-wrapper {
  margin-bottom: 24px;
  display: flex;
  justify-content: center;
}

.preview-image {
  max-width: 100%;
  max-height: 400px;
  border: 2px solid #ddd;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.action-buttons {
  display: flex;
  gap: 12px;
  justify-content: right;
  //margin-bottom: 24px;
  flex-wrap: wrap;
  border-top: none;
  margin-top:0 ;
}

/* Approval Section */
.approval-section {
  margin: 0;
  padding: 0;
  background: #fff;
  border-radius: 8px;
  //border: 2px solid #e0e0e0;
}

.approval-checkbox {
  display: flex;
  align-items: center;
  justify-content: left;
  gap: 12px;
  cursor: pointer;
  user-select: none;
}

.approval-checkbox input[type="checkbox"] {
  width: 20px;
  height: 20px;
  cursor: pointer;
  flex-shrink: 0;
  //accent-color: #0066cc;
}

.checkbox-label {
  font-size: 14px;
  color: #252525;
  line-height: 1.5;
  font-weight: 500;
}

.approval-checkbox:hover .checkbox-label {
  color: #252525;
}

.approval-status {
  margin-top: 12px;
  padding: 12px;
  background: #d4edda;
  border: 1px solid #c3e6cb;
  border-radius: 6px;
  color: #155724;
  font-weight: 500;
  font-size: 14px;
}

/* Test Controls */
.test-controls {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #ddd;
}

/* Debug Info */
.debug-info {
  margin-top: 20px;
  text-align: left;
}

.debug-info details {
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 12px;
}

.debug-info summary {
  cursor: pointer;
  font-weight: 500;
  color: #666;
  user-select: none;
}

.debug-info summary:hover {
  color: #0066cc;
}

.debug-info pre {
  margin: 12px 0 0 0;
  padding: 12px;
  background: #f5f5f5;
  border-radius: 4px;
  font-size: 12px;
  overflow-x: auto;
  white-space: pre-wrap;
  word-wrap: break-word;
}

/* Template Select */
.template-select-state {
  text-align: center;
}

.template-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  justify-content: center;
  margin: 20px 0;
}

.template-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 12px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
  width: 160px;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.template-card:hover {
  border-color: #0066cc;
  box-shadow: 0 2px 8px rgba(0, 102, 204, 0.15);
}

.template-card__img {
  width: 130px;
  height: 130px;
  object-fit: contain;
  border-radius: 4px;
}

.template-card__placeholder {
  width: 130px;
  height: 130px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
  border-radius: 4px;
  font-size: 12px;
  color: #999;
}

.template-card__title {
  font-size: 13px;
  color: #333;
  font-weight: 500;
  text-align: center;
  line-height: 1.3;
}

/* Responsive */
@media (max-width: 768px) {
  .design-online-simple {
    padding: 16px;
  }

  .button-group,
  .action-buttons {
    flex-direction: column;
    align-items: stretch;
  }

  .approval-checkbox {
    flex-direction: column;
    text-align: center;
  }

  .preview-image {
    max-height: 200px;
  }
}
</style>
